import React, { useState } from "react";
import { domToBlob } from "modern-screenshot";
import { Camera } from "@phosphor-icons/react";
import { toast } from "sonner";

const EXCLUDE = [
  "leaflet-control-container",
  "map-screenshot-exclude",
];

function shouldExclude(node) {
  if (!node.classList) return false;
  return EXCLUDE.some((c) => node.classList.contains(c));
}

function downloadBlob(blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `okanogan-fire-map-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function stampWatermark(blob) {
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, 0, 0);

  const u = Math.max(bmp.width / 1400, 0.75);
  const brand = "OKANOGAN HIGHLANDS FIREWATCH";
  const ts = new Date().toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
  const brandFont = `bold ${Math.round(15 * u)}px Arial, sans-serif`;
  const tsFont = `${Math.round(12 * u)}px Arial, sans-serif`;
  ctx.font = brandFont;
  const brandW = ctx.measureText(brand).width;
  ctx.font = tsFont;
  const tsW = ctx.measureText(ts).width;

  const padX = 12 * u;
  const gap = 10 * u;
  const flameW = 14 * u;
  const boxW = padX + flameW + 7 * u + brandW + gap + tsW + padX;
  const boxH = 32 * u;
  const x = 12 * u;
  const y = canvas.height - boxH - 12 * u;

  ctx.fillStyle = "rgba(15, 19, 17, 0.82)";
  ctx.beginPath();
  ctx.roundRect(x, y, boxW, boxH, 4 * u);
  ctx.fill();

  const midY = y + boxH / 2;
  ctx.font = `${Math.round(15 * u)}px Arial`;
  ctx.textBaseline = "middle";
  ctx.fillText("🔥", x + padX, midY + 1 * u);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = brandFont;
  ctx.fillText(brand, x + padX + flameW + 7 * u, midY);
  ctx.fillStyle = "rgba(255,255,255,0.65)";
  ctx.font = tsFont;
  ctx.fillText(ts, x + padX + flameW + 7 * u + brandW + gap, midY);

  bmp.close();
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("watermark failed"))), "image/png");
  });
}

export async function captureMapScreenshot(map) {
  if (!map) return;
  const container = map.getContainer();
  const blobPromise = domToBlob(container, {
    type: "image/png",
    scale: 1,
    filter: (node) => !shouldExclude(node),
    fetch: { requestInit: { mode: "cors", cache: "force-cache" } },
    features: { removeControlCharacter: false },
  }).then((blob) => {
    if (!(blob instanceof Blob) || blob.size === 0) throw new Error("capture failed");
    return stampWatermark(blob);
  });

  if (navigator.clipboard && window.ClipboardItem) {
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blobPromise })]);
      toast.success("Map screenshot copied to clipboard", {
        description: "Paste it straight into a Facebook post (Ctrl+V).",
      });
      return;
    } catch (e) {
      // clipboard blocked — fall back to download
    }
  }
  try {
    const blob = await blobPromise;
    downloadBlob(blob);
    toast.success("Map screenshot downloaded", {
      description: "Clipboard wasn't available, so it saved as a PNG instead.",
    });
  } catch (e) {
    toast.error("Screenshot failed", { description: "Try a different base map layer and retry." });
  }
}

export function ScreenshotButton({ map, variant = "icon" }) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (busy || !map) return;
    setBusy(true);
    try {
      await captureMapScreenshot(map);
    } finally {
      setBusy(false);
    }
  };

  if (variant === "row") {
    return (
      <button
        type="button"
        data-testid="map-screenshot-row-btn"
        onClick={run}
        disabled={busy || !map}
        className="w-full flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text hover:text-tac-primary transition-colors disabled:opacity-50"
      >
        <Camera size={13} weight="bold" className="text-[#4CC9F0]" />
        {busy ? "Capturing…" : "Screenshot Map"}
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid="map-screenshot-btn"
      title="Copy a screenshot of the map to your clipboard"
      onClick={run}
      disabled={busy || !map}
      className="px-2.5 py-1.5 rounded-sm border bg-tac-bg/85 backdrop-blur-xl border-tac-border text-tac-text hover:border-tac-borderBright transition-colors disabled:opacity-50"
    >
      <Camera size={14} weight={busy ? "fill" : "bold"} className={busy ? "animate-pulse" : ""} />
    </button>
  );
}
